/**
 * Colour and size on a product card.
 *
 * The card starts as it always did: a link to the product reading Choose
 * Options. Picking a colour swaps the photo and offers the sizes that exist
 * in that colour; picking a size turns the link into a real add-to-cart form.
 * With the script off none of this appears and the link is what remains,
 * which is the behaviour the card had before.
 *
 * The chips are drawn from the variants rather than from the option's
 * values, because which sizes exist depends on the colour, and offering one
 * that was never made is worse than not offering it. A product listed with
 * one option has no colour to pick first, so its chips are there from the
 * start.
 *
 * Each variant arrives labelled with its colour and its other choice, which
 * is Size on a tee and Denominations on the gift card. Working that out here
 * by elimination assumed every product had both, and named.
 */
(() => {
  const SWATCHES = {
    Ash: '#b9b9b4', Asphalt: '#4b4d4d', 'Athletic Heather': '#cbcbcb', Black: '#101010',
    'Black Heather': '#2b2b2b', Bone: '#e8e0d0', Brown: '#5b4438', 'Carbon Grey': '#65676a',
    Cardinal: '#9b2033', 'Carolina Blue': '#8ab4dd', 'Charcoal Heather': '#4a4a4a',
    'Dark Grey': '#4d4d4d', 'Dark Heather': '#454545', 'Heather Aqua': '#8fd3d6',
    'Heather Emerald': '#2f8f6b', 'Heather Grass Green': '#7bab5a',
    'Heather Midnight Navy': '#28374b', 'Heather Mint': '#a9ddc4', 'Heather Natural': '#e0d9c8',
    'Heather Navy': '#39465c', 'Heather Prism Dusty Blue': '#8fa8bd',
    'Heather Prism Ice Blue': '#c6dde8', 'Heather Prism Mint': '#bfe3cf', 'Heather Red': '#bd3a3a',
    'Heather True Royal': '#2f5aa8', Lavender: '#c4b3d9', 'Light Blue': '#a9c9e3',
    Maroon: '#6b2233', Mauve: '#b18f96', Natural: '#e5ddca', Navy: '#242f45',
    'Navy Blazer': '#28324a', 'Oatmeal Heather': '#ded5c2', 'Ocean Blue': '#2f6f97',
    Pebble: '#cfc6b8', Pink: '#e5a4bb', Royal: '#2a4fa2', Sage: '#9aa98b', Silver: '#c8c9cb',
    'Sky Blue': '#9ecbe8', 'Soft Pink': '#f0c9d4', 'Solid White Blend': '#f6f6f4',
    'Sport Grey': '#b0b0ae', 'Steel Blue': '#6e8fa8', 'Team Purple': '#4b2e7a',
    'Team Royal': '#23459a', 'True Royal': '#2549a0', 'Vintage Black': '#2a2a2a',
    'Vintage Navy': '#333f52', 'Vintage White': '#efeade', White: '#ffffff', Yellow: '#f2cb46',
  };

  const setup = (card) => {
    if (card.dataset.cardReady) return;
    const data = card.querySelector('[data-card-variants]');
    const choices = card.querySelector('[data-card-choices]');
    if (!data || !choices) return;

    // A product sold in one colour has no swatch row at all.
    const colors = card.querySelector('[data-card-colors]');

    let variants;
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }
    card.dataset.cardReady = 'true';

    const image = card.querySelector('[data-card-image-wrap] img');
    const prev = card.querySelector('[data-card-prev]');
    const next = card.querySelector('[data-card-next]');

    // Every photograph with the colour it belongs to, so the arrows page
    // through the shirt on show rather than through every colour made.
    let photos = [];
    const photoData = card.querySelector('[data-card-images]');
    if (photoData) {
      try {
        photos = JSON.parse(photoData.textContent);
      } catch (e) {
        photos = [];
      }
    }
    let reel = [];
    let at = 0;
    const price = card.querySelector('[data-card-price]');
    // What the card says before anything is picked: the product's lowest
    // price, which is where it goes back to when a choice is undone.
    const basePrice = price ? price.textContent : '';
    const choose = card.querySelector('[data-card-choose]');
    const form = card.querySelector('[data-card-add]');
    const idField = card.querySelector('[data-card-variant-id]');

    let chosenColor = null;
    let chosenChoice = null;

    // paint the swatches from the names, since the shop has no swatch set
    card.querySelectorAll('[data-swatch]').forEach((dot) => {
      const hex = SWATCHES[dot.dataset.swatch];
      if (hex) dot.style.backgroundColor = hex;
      else dot.textContent = dot.dataset.swatch.slice(0, 2);
    });

    const variantFor = (color, choice) =>
      variants.find((v) => v.color === color && v.choice === choice);

    const showAdd = (variant) => {
      if (!form || !choose) return;
      const ready = variant && variant.available;
      idField.value = ready ? variant.id : '';
      form.classList.toggle('hidden', !ready);
      choose.classList.toggle('hidden', !!ready);
    };

    // The gift card is the case that makes this visible: its four amounts
    // are four prices, so choosing $50 while the card still read $10 was the
    // card contradicting itself.
    // The price changing is easy to miss when the eye is on the size that
    // was just pressed, so it says so and goes back to white. Same as the
    // product page; the animation ends where the element already is, so a
    // browser that skips it strands nothing.
    const showPrice = (variant) => {
      if (!price) return;
      const next = variant && variant.price ? variant.price : basePrice;
      if (next === price.textContent.trim()) return;
      price.textContent = next;
      price.classList.remove('price-changed');
      // Reading the layout is what restarts an animation that is already
      // on the element: without it, two changes in a row only blink once.
      void price.offsetWidth;
      price.classList.add('price-changed');
    };

    // The widths a card asks for, built from the one URL the page carries.
    // Every Shopify image URL arrives with width= on it, so the others are
    // the same address with a different number.
    const srcsetFor = (src) =>
      [300, 600, 900].map((w) => `${src.replace(/width=\d+/, 'width=' + w)} ${w}w`).join(', ');

    /* While a photograph is on its way, the name fills with the colour that
     * was asked for, and empties again when it arrives.
     *
     * Only after 120ms. One already in the cache is there in a few
     * milliseconds, and a flash of colour on every click is noise rather
     * than an answer to "is it doing anything".
     */
    const title = card.querySelector('h3');
    let waitTimer = null;

    const settled = () => {
      clearTimeout(waitTimer);
      if (title) title.removeAttribute('data-loading');
    };

    /* The swatch's own colour, pushed into a band where it can be read.
     *
     * Straight from the swatch it does not work at either end: black
     * heather is #2b2b2b and fills the name with dark grey on a dark page,
     * which reads as the name disappearing rather than as the colour
     * arriving, and white fills white on white and says nothing at all. So
     * the hue is kept and only the lightness is moved, by mixing with white
     * or with black until it sits between the two.
     */
    const INK_FLOOR = 0.36;
    const INK_CEILING = 0.72;

    const inkFor = (hex) => {
      if (!hex || hex.length !== 7) return null;
      let rgb = [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16),
      ];
      if (rgb.some(isNaN)) return null;

      const light = (c) => (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255;
      const mix = (c, towards, amount) => c.map((v) => Math.round(v + (towards - v) * amount));

      // Up to nine steps of a tenth each, which is enough to lift #000 over
      // the floor and to bring #fff under the ceiling.
      for (let i = 0; i < 9 && light(rgb) < INK_FLOOR; i += 1) rgb = mix(rgb, 255, 0.1);
      for (let i = 0; i < 9 && light(rgb) > INK_CEILING; i += 1) rgb = mix(rgb, 0, 0.1);

      return '#' + rgb.map((v) => v.toString(16).padStart(2, '0')).join('');
    };

    const waitFor = (shot) => {
      if (!title || !image) return;
      clearTimeout(waitTimer);
      const ink = inkFor(SWATCHES[shot.color]);
      waitTimer = setTimeout(() => {
        if (image.complete && image.naturalWidth > 0) return;
        if (ink) title.style.setProperty('--text-fill-loading', ink);
        else title.style.removeProperty('--text-fill-loading');
        title.dataset.loading = '';
      }, 120);
    };

    if (image) {
      image.addEventListener('load', settled);
      image.addEventListener('error', settled);
    }

    const showPhoto = (shot) => {
      if (!image || !shot || !shot.src) return;
      waitFor(shot);
      // srcset first: with one in place the browser chooses from it and
      // never reads src, so setting src alone changed nothing.
      image.srcset = srcsetFor(shot.src);
      image.src = shot.src;
    };

    const paintArrows = () => {
      const many = reel.length > 1;
      if (prev) prev.hidden = !many;
      if (next) next.hidden = !many;
    };

    const showAt = (index) => {
      if (!image || !reel.length) return;
      at = (index + reel.length) % reel.length;
      showPhoto(reel[at]);
    };

    // The reel is the run of photographs for one colour. Without a colour,
    // which is a product sold in one, it is everything the product has.
    const loadReel = (colour) => {
      if (!photos.length) return;
      const wanted = colour ? photos.filter((p) => p.color === colour) : photos;
      reel = wanted.length ? wanted : photos;
      at = 0;
      paintArrows();
    };

    if (prev) prev.addEventListener('click', () => showAt(at - 1));
    if (next) next.addEventListener('click', () => showAt(at + 1));

    /* Drag, or swipe, to the next photograph.
     *
     * The card shows one <img> and swaps its source, so there is no track to
     * scroll: this reads the gesture and calls the same thing the arrows
     * call. Pointer events, so a mouse and a finger are the same code.
     *
     * The photograph is inside the link to the product, which is what makes
     * this delicate. dragstart is cancelled, or the browser starts its own
     * drag and drop the moment the pointer moves on an image and our events
     * stop arriving. And a drag that ends on the link must not also follow
     * it, so a click after a real drag is swallowed. What is NOT done is
     * preventDefault on pointerdown: on a link that also suppresses the
     * click, and then a plain click stopped opening the product.
     *
     * touch-action: pan-y in the stylesheet is the other half. It leaves
     * vertical scrolling to the browser and sends horizontal gestures here,
     * which is why this never has to guess whether the page should scroll.
     */
    const frame = card.querySelector('[data-card-image-wrap]');
    if (frame) {
      const THRESHOLD = 40;
      let startX = 0;
      let startY = 0;
      let dragging = false;
      let moved = 0;
      let swallowClick = false;

      frame.addEventListener('dragstart', (event) => event.preventDefault());

      frame.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (reel.length < 2) return;
        dragging = true;
        moved = 0;
        startX = event.clientX;
        startY = event.clientY;
      });

      frame.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        // A gesture that is mostly vertical is the page being scrolled.
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
          dragging = false;
          return;
        }
        moved = dx;
      });

      const release = () => {
        if (!dragging) return;
        dragging = false;
        if (Math.abs(moved) >= THRESHOLD) {
          showAt(moved < 0 ? at + 1 : at - 1);
          swallowClick = true;
          setTimeout(() => {
            swallowClick = false;
          }, 100);
        }
        moved = 0;
      };

      frame.addEventListener('pointerup', release);
      frame.addEventListener('pointercancel', release);
      frame.addEventListener('pointerleave', release);

      frame.addEventListener(
        'click',
        (event) => {
          if (!swallowClick) return;
          swallowClick = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }

    // Five, then a link, the way the colours are capped. A product with no
    // colour shows its chips from the start -- there is no colour to wait
    // for -- and nine of them beside cards carrying five dots made the two
    // look like different card designs. Five of each reads as one.
    const CHIP_LIMIT = 5;

    const renderChoices = () => {
      choices.innerHTML = '';
      const pool = variants.filter((v) => v.color === chosenColor && v.choice);
      const shown = pool.slice(0, CHIP_LIMIT);
      shown.forEach((v) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'filter-option filter-option--chip';
        button.textContent = v.choice;
        button.disabled = !v.available;
        if (!v.available) button.classList.add('opacity-40');
        button.addEventListener('click', () => {
          chosenChoice = v.choice;
          choices.querySelectorAll('button').forEach((b) => b.classList.remove('is-chosen'));
          button.classList.add('is-chosen');
          // The chip has a photo of its own where the colour does not: the
          // gift card is four pictures, one per amount, and nothing else to
          // pick. On a tee every size carries the colour's photo, so this
          // shows what is already there.
          showPrice(v);
          showAdd(variantFor(chosenColor, chosenChoice));
        });
        li.appendChild(button);
        choices.appendChild(li);
      });

      if (pool.length > CHIP_LIMIT) {
        const li = document.createElement('li');
        const more = document.createElement('a');
        more.href = card.querySelector('a[href*="/products/"]').getAttribute('href');
        more.className = 'font-lato text-[13px] font-light text-white/60 underline';
        more.textContent = '+' + (pool.length - CHIP_LIMIT);
        li.appendChild(more);
        choices.appendChild(li);
      }

      choices.classList.toggle('hidden', pool.length === 0);
      choices.classList.toggle('flex', pool.length > 0);
    };

    if (colors) {
      colors.querySelectorAll('[data-card-color]').forEach((button) => {
        button.addEventListener('click', () => {
          const value = button.dataset.cardColor;
          const reselecting = chosenColor === value;
          colors.querySelectorAll('[data-card-color]').forEach((b) => b.classList.remove('is-chosen'));
          chosenChoice = null;

          if (reselecting) {
            chosenColor = null;
            choices.classList.add('hidden');
            choices.classList.remove('flex');
            showPrice(null);
            showAdd(null);
            return;
          }

          chosenColor = value;
          button.classList.add('is-chosen');
          loadReel(value);
          showAt(0);
          renderChoices();
          showPrice(variantFor(value, null));
          // A product sold only by colour is settled by the swatch itself:
          // that variant carries no second choice, so this finds it, and
          // where there is one to make it finds nothing and waits.
          showAdd(variantFor(value, null));
        });
      });
    } else {
      // Nothing to pick first: chosenColor stays null, which is what the
      // variants of a single-option product carry.
      renderChoices();
    }

    // The card opens on the colour its own photograph belongs to, so the
    // arrows page through that shirt rather than through the lot.
    if (photos.length && image) {
      const src = image.getAttribute('src');
      const opening = photos.find((p) => p.src === src);
      loadReel(opening ? opening.color : photos[0].color);
      const here = reel.findIndex((p) => p.src === src);
      at = here < 0 ? 0 : here;
    }
  };

  const scan = () => document.querySelectorAll('[data-product-card]').forEach(setup);

  scan();
  // the collection page replaces its results when filters change
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
})();
